import React, { useMemo, useState } from 'react';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value || 0);

const round = (n) => Math.round(n || 0);

const computeMonto = (concept, salario, totalDevengado) => {
    if (concept.tipoCalculo === 'porcentaje') {
        const base = concept.baseCalculo === 'totalDevengado' ? totalDevengado : salario;
        return round((base * (Number(concept.valor) || 0)) / 100);
    }
    return round(Number(concept.valor) || 0);
};

let ROW_SEQ = 0;
const nextId = () => `row-${ROW_SEQ++}`;

/**
 * Editor de liquidación: construye devengados/deducciones editables a partir
 * de las horas calculadas y los conceptos parametrizados. Cada fila puede
 * editarse o eliminarse, y se pueden agregar conceptos manuales o parametrizados.
 *
 * Props: employee, lavanderia, salario, valorHoraBase, multipliers, totals,
 *        payrollData, startDate, endDate, horasALiquidar, nightShiftStart,
 *        nightShiftEnd, concepts, user, saving, onSave(payload), onClose()
 */
const LiquidacionEditor = ({
    employee,
    salario,
    valorHoraBase,
    multipliers,
    totals,
    payrollData,
    startDate,
    endDate,
    horasALiquidar,
    nightShiftStart,
    nightShiftEnd,
    concepts = [],
    user,
    saving = false,
    onSave,
    onClose,
}) => {
    const lavanderia = employee?.lavanderia || {};

    // Estado inicial calculado una sola vez al montar
    const initial = useMemo(() => {
        const hourDefs = [
            ['Horas base', totals.horasBase, valorHoraBase, 'horaBase'],
            ['Horas extra diurnas', totals.horasExtrasDiurnas, valorHoraBase * multipliers.horaExtraDiurna, 'horaExtraDiurna'],
            ['Horas extra nocturnas', totals.horasExtrasNocturnas, valorHoraBase * multipliers.horaExtraNocturna, 'horaExtraNocturna'],
            ['Recargo nocturno', totals.recargoNocturno, valorHoraBase * multipliers.recargoNocturno, 'recargoNocturno'],
            ['Festivo diurno', totals.festivoDiurno, valorHoraBase * multipliers.festivoDiurno, 'festivoDiurno'],
            ['Festivo nocturno', totals.festivoNocturno, valorHoraBase * multipliers.festivoNocturno, 'festivoNocturno'],
        ];
        const hourRows = hourDefs
            .filter(([, h]) => h > 0)
            .map(([nombre, horas, vHora]) => ({
                id: nextId(),
                nombre,
                naturaleza: 'devengado',
                origen: 'horas',
                horas: Math.round(horas * 100) / 100,
                monto: round(horas * vHora),
            }));

        const baseHoras = hourRows.reduce((s, r) => s + r.monto, 0);

        const devengadoConcepts = concepts
            .filter((c) => c.naturaleza === 'devengado')
            .map((c) => ({
                id: nextId(),
                codigo: c.codigo || '',
                nombre: c.nombre,
                naturaleza: 'devengado',
                origen: 'concepto',
                tipoCalculo: c.tipoCalculo,
                valor: c.valor,
                monto: computeMonto(c, salario, baseHoras),
            }));

        const totalDevengadoInicial =
            baseHoras + devengadoConcepts.reduce((s, r) => s + r.monto, 0);

        const deduccionConcepts = concepts
            .filter((c) => c.naturaleza === 'deduccion')
            .map((c) => ({
                id: nextId(),
                codigo: c.codigo || '',
                nombre: c.nombre,
                naturaleza: 'deduccion',
                origen: 'concepto',
                tipoCalculo: c.tipoCalculo,
                valor: c.valor,
                monto: computeMonto(c, salario, totalDevengadoInicial),
            }));

        return { hourRows, extraDevengados: devengadoConcepts, deducciones: deduccionConcepts, baseHoras };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [hourRows, setHourRows] = useState(initial.hourRows);
    const [extraDevengados, setExtraDevengados] = useState(initial.extraDevengados);
    const [deducciones, setDeducciones] = useState(initial.deducciones);
    const [consolidar, setConsolidar] = useState(false);
    const [consolidatedMonto, setConsolidatedMonto] = useState(initial.baseHoras);

    const sumHoras = hourRows.reduce((s, r) => s + (Number(r.monto) || 0), 0);
    const devengadoHorasMonto = consolidar ? Number(consolidatedMonto) || 0 : sumHoras;
    const totalDevengados =
        devengadoHorasMonto + extraDevengados.reduce((s, r) => s + (Number(r.monto) || 0), 0);
    const totalDeducciones = deducciones.reduce((s, r) => s + (Number(r.monto) || 0), 0);
    const totalNeto = totalDevengados - totalDeducciones;

    /* --- handlers de edición --- */
    const updateRow = (setter) => (id, field, value) =>
        setter((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    const removeRow = (setter) => (id) => setter((rows) => rows.filter((r) => r.id !== id));

    const updHour = updateRow(setHourRows);
    const updDev = updateRow(setExtraDevengados);
    const updDed = updateRow(setDeducciones);

    const toggleConsolidar = () => {
        if (!consolidar) setConsolidatedMonto(sumHoras);
        setConsolidar((v) => !v);
    };

    const addFromConcept = (naturaleza, conceptId) => {
        const c = concepts.find((x) => x._id === conceptId);
        if (!c) return;
        const row = {
            id: nextId(),
            codigo: c.codigo || '',
            nombre: c.nombre,
            naturaleza,
            origen: 'concepto',
            tipoCalculo: c.tipoCalculo,
            valor: c.valor,
            monto: computeMonto(c, salario, totalDevengados),
        };
        if (naturaleza === 'devengado') setExtraDevengados((r) => [...r, row]);
        else setDeducciones((r) => [...r, row]);
    };

    const addManual = (naturaleza) => {
        const row = { id: nextId(), codigo: '', nombre: '', naturaleza, origen: 'manual', monto: 0 };
        if (naturaleza === 'devengado') setExtraDevengados((r) => [...r, row]);
        else setDeducciones((r) => [...r, row]);
    };

    const handleSave = () => {
        const devengadoRows = consolidar
            ? [{
                nombre: 'Salario por horas trabajadas',
                naturaleza: 'devengado',
                origen: 'horas',
                horas: payrollData?.summary?.totalHorasAutorizadas,
                monto: round(consolidatedMonto),
            }]
            : hourRows;

        const allConcepts = [...devengadoRows, ...extraDevengados, ...deducciones].map((r) => ({
            codigo: r.codigo || '',
            nombre: r.nombre,
            naturaleza: r.naturaleza,
            tipoCalculo: r.tipoCalculo || 'fijo',
            valor: Number(r.valor) || 0,
            horas: r.horas,
            montoCalculado: round(r.monto),
        }));

        const payload = {
            empleado: {
                id: employee._id,
                nombre: `${employee.nombre || ''} ${employee.apellido || ''}`.trim(),
                cedula: employee.cedula,
                rol: employee.rol,
                oficio: employee.rol,
                ...(employee.banco ? { banco: employee.banco } : {}),
                ...(employee.cuenta ? { cuenta: employee.cuenta } : {}),
            },
            lavanderia: { id: lavanderia.id, nombre: lavanderia.nombre },
            periodo: { fechaInicio: startDate, fechaFin: endDate },
            salario: Number(salario) || 0,
            configuracion: {
                horasALiquidar,
                horarioNocturno: { inicio: nightShiftStart, fin: nightShiftEnd },
                multiplicadores: { ...multipliers },
            },
            horas: {
                horasBase: totals.horasBase,
                horasExtrasDiurnas: totals.horasExtrasDiurnas,
                horasExtrasNocturnas: totals.horasExtrasNocturnas,
                recargoNocturno: totals.recargoNocturno,
                festivoDiurno: totals.festivoDiurno,
                festivoNocturno: totals.festivoNocturno,
                totalHorasTrabajadas: payrollData?.summary?.totalHorasTrabajadas || 0,
                totalHorasAutorizadas: payrollData?.summary?.totalHorasAutorizadas || 0,
            },
            valores: {
                valorHoraBase,
                valorHorasBase: round(totals.horasBase * valorHoraBase),
                valorHorasExtrasDiurnas: round(totals.horasExtrasDiurnas * valorHoraBase * multipliers.horaExtraDiurna),
                valorHorasExtrasNocturnas: round(totals.horasExtrasNocturnas * valorHoraBase * multipliers.horaExtraNocturna),
                valorRecargoNocturno: round(totals.recargoNocturno * valorHoraBase * multipliers.recargoNocturno),
                valorFestivoDiurno: round(totals.festivoDiurno * valorHoraBase * multipliers.festivoDiurno),
                valorFestivoNocturno: round(totals.festivoNocturno * valorHoraBase * multipliers.festivoNocturno),
                totalDevengados: round(totalDevengados),
                totalDeducciones: round(totalDeducciones),
                totalNeto: round(totalNeto),
                totalAPagar: round(totalNeto),
            },
            conceptos: allConcepts,
            registros: payrollData?.records || [],
            totalDias: payrollData?.summary?.totalDias || 0,
            estado: 'Pendiente',
            creadoPor: user ? { id: user.id, nombre: user.name } : undefined,
        };
        onSave?.(payload);
    };

    const availableDevengados = concepts.filter((c) => c.naturaleza === 'devengado');
    const availableDeducciones = concepts.filter((c) => c.naturaleza === 'deduccion');

    const renderMontoInput = (row, updater) => (
        <Input
            type="number"
            className="w-32 h-8 text-right"
            value={row.monto}
            onChange={(e) => updater(row.id, 'monto', Number(e.target.value))}
        />
    );

    return (
        <Card className="border-2 border-blue-200">
            <CardHeader>
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
                    <div>
                        <CardTitle>Generar Liquidación</CardTitle>
                        <CardDescription>
                            {employee?.nombre} {employee?.apellido} • Centro: {lavanderia?.nombre || '—'} •{' '}
                            Período {startDate} a {endDate}
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                            <Checkbox checked={consolidar} onCheckedChange={toggleConsolidar} />
                            Consolidar horas en una sola fila
                        </label>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* DEVENGADOS */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-green-700">Devengados (+)</h3>
                            <div className="flex items-center gap-2">
                                <Select onValueChange={(v) => addFromConcept('devengado', v)} value="">
                                    <SelectTrigger className="w-44 h-8">
                                        <SelectValue placeholder="+ Agregar concepto" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableDevengados.map((c) => (
                                            <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button variant="outline" size="sm" onClick={() => addManual('devengado')}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Concepto</TableHead>
                                        <TableHead>Horas</TableHead>
                                        <TableHead className="text-right">Valor</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {consolidar ? (
                                        <TableRow>
                                            <TableCell className="font-medium">Salario por horas trabajadas</TableCell>
                                            <TableCell>{payrollData?.summary?.totalHorasAutorizadas ?? '—'}h</TableCell>
                                            <TableCell className="text-right">
                                                <Input
                                                    type="number"
                                                    className="w-32 h-8 text-right ml-auto"
                                                    value={consolidatedMonto}
                                                    onChange={(e) => setConsolidatedMonto(Number(e.target.value))}
                                                />
                                            </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    ) : (
                                        hourRows.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell className="font-medium">{row.nombre}</TableCell>
                                                <TableCell>{row.horas}h</TableCell>
                                                <TableCell className="text-right">{renderMontoInput(row, updHour)}</TableCell>
                                                <TableCell>
                                                    <Button variant="ghost" size="sm" onClick={() => removeRow(setHourRows)(row.id)}>
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                    {extraDevengados.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                {row.origen === 'manual' ? (
                                                    <Input
                                                        className="h-8"
                                                        placeholder="Nombre del concepto"
                                                        value={row.nombre}
                                                        onChange={(e) => updDev(row.id, 'nombre', e.target.value)}
                                                    />
                                                ) : (
                                                    <span className="font-medium">{row.nombre}</span>
                                                )}
                                            </TableCell>
                                            <TableCell>—</TableCell>
                                            <TableCell className="text-right">{renderMontoInput(row, updDev)}</TableCell>
                                            <TableCell>
                                                <Button variant="ghost" size="sm" onClick={() => removeRow(setExtraDevengados)(row.id)}>
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {hourRows.length === 0 && extraDevengados.length === 0 && !consolidar && (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center text-muted-foreground h-16">
                                                Sin devengados
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex justify-between items-center mt-2 px-1">
                            <span className="font-semibold text-green-700">TOTAL DEVENGADOS</span>
                            <span className="font-bold text-green-700">{formatCurrency(totalDevengados)}</span>
                        </div>
                    </div>

                    {/* DEDUCCIONES */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-red-700">Deducciones (−)</h3>
                            <div className="flex items-center gap-2">
                                <Select onValueChange={(v) => addFromConcept('deduccion', v)} value="">
                                    <SelectTrigger className="w-44 h-8">
                                        <SelectValue placeholder="+ Agregar concepto" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableDeducciones.map((c) => (
                                            <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button variant="outline" size="sm" onClick={() => addManual('deduccion')}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Concepto</TableHead>
                                        <TableHead className="text-right">Valor</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {deducciones.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                {row.origen === 'manual' ? (
                                                    <Input
                                                        className="h-8"
                                                        placeholder="Nombre del concepto"
                                                        value={row.nombre}
                                                        onChange={(e) => updDed(row.id, 'nombre', e.target.value)}
                                                    />
                                                ) : (
                                                    <span className="font-medium">{row.nombre}</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">{renderMontoInput(row, updDed)}</TableCell>
                                            <TableCell>
                                                <Button variant="ghost" size="sm" onClick={() => removeRow(setDeducciones)(row.id)}>
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {deducciones.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center text-muted-foreground h-16">
                                                Sin deducciones
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex justify-between items-center mt-2 px-1">
                            <span className="font-semibold text-red-700">TOTAL DEDUCCIONES</span>
                            <span className="font-bold text-red-700">{formatCurrency(totalDeducciones)}</span>
                        </div>
                    </div>
                </div>

                {/* TOTAL NETO */}
                <div className="flex justify-end">
                    <div className="flex items-center gap-4 p-4 border-2 border-blue-300 rounded-lg bg-blue-50">
                        <span className="text-lg font-semibold text-blue-900">TOTAL A PAGAR</span>
                        <span className="text-2xl font-bold text-blue-900">{formatCurrency(totalNeto)}</span>
                    </div>
                </div>

                <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={onClose}>
                        <X className="mr-2 h-4 w-4" /> Cancelar
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        <Save className="mr-2 h-4 w-4" />
                        {saving ? 'Guardando...' : 'Guardar Liquidación'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default LiquidacionEditor;
